/**
 * Input component with compound sub-components for file input.
 *
 * Base Input: text/email/password/number/search/file with leading/trailing slots, multiline mode.
 * File input is composable via useFileInput hook + Input.Dropzone + Input.FileList.
 *
 * Usage:
 *   const [state, actions] = useFileInput({ fileType: 'image', multiple: true })
 *   <Input type="file" fileInputActions={actions}>
 *     <Input.Dropzone state={state} actions={actions} />
 *   </Input>
 *   <Input.FileList state={state} actions={actions} />
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import type { InputProps } from './input.types'

import { Input as InputPrimitive } from '@base-ui/react/input'
import type * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/utils'
import {
  ClipboardPasteIcon,
  FileArchiveIcon,
  FileCheckCornerIcon,
  FileIcon,
  FileImageIcon,
  FileMusicIcon,
  FilePlusCornerIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoCameraIcon,
  MicIcon,
  PauseIcon,
  PlayIcon,
  SquareIcon,
  Trash2Icon,
  VideoIcon,
  YoutubeIcon,
} from './icon'
import { IconButton } from './icon-button'
import { Button } from './button'
import { bytes, type UFile } from '@w/utils'
import { useTranslation } from '../i18n'
import type {
  FileInputState,
  FileInputActions,
  FileType,
} from '../hooks/use-file-input'


function Input({
  className,
  leading,
  trailing,
  multiline,
  minimal,
  filled,
  maxHeight,
  inputRef,
  fileInputState,
  fileInputActions,
  children,
  'data-testid': dataTestId,
  ...props
}: InputProps & { 'data-testid'?: string }) {
  const internalRef = useRef<HTMLSpanElement>(null)
  // File input provides its own ref (shared with hook); text input uses internal
  const containerRef = fileInputActions?.containerRef ?? internalRef
  const isFile = props.type === 'file'
  const isRejected = fileInputState?.isRejected ?? false

  const inputClassName = cn(
    'placeholder:text-on-surface-secondary aria-invalid:placeholder:text-error/60 w-full leading-none outline-none placeholder:select-none disabled:cursor-not-allowed',
    filled && 'aria-invalid:placeholder:text-on-error/60',
    multiline &&
      `field-sizing-content min-h-[52px] resize-none ${trailing ? '' : 'pr-4'}`,
    props.type === 'number' &&
      '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
    props.type === 'search' &&
      '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none',
    props.type === 'file' && 'sr-only',
  )

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- span delegates clicks to inner input; keyboard handled by the input itself
    <span
      ref={containerRef}
      className={
        cn(
          minimal
            ? inputMinimalClass
            : filled
              ? inputFilledClass
              : inputContainerClass,
          props.type === 'search' &&
            'bg-surface-3 rounded-full! border-none outline-none has-focus-visible:border-none! has-focus-visible:ring-0!',
          props.type === 'file' && 'min-h-20 cursor-pointer! border-dashed',
          isRejected && 'border-error! bg-surface-error! opacity-60',
          multiline
            ? `items-start py-4 ${trailing ? '' : 'pr-0'}`
            : 'items-center',
          isFile && 'py-8',
          // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: ReactNode || ReactNode for JSX presence check
          (leading || trailing) && 'gap-3',
          className,
        ) || undefined
      }
      data-slot="input-control"
      data-testid={isFile ? dataTestId : undefined}
      {...(isFile && fileInputActions ? fileInputActions.dragHandlers : {})}
      onClick={(e) => {
        const target = e.target as HTMLElement
        // Don't steal clicks from interactive elements or the input itself
        if (target.closest('button, a, [role="button"]')) return
        if (target.closest('[data-slot="input"]')) return
        const input = (
          containerRef as React.RefObject<HTMLSpanElement>
        ).current?.querySelector<HTMLInputElement>('[data-slot="input"]')
        if (isFile) {
          input?.click()
        } else {
          input?.focus()
        }
      }}
    >
      {leading && (
        <span
          className="text-outline flex shrink-0 items-center [&_svg:not([class*='size-'])]:size-5"
          data-slot="input-leading"
        >
          {leading}
        </span>
      )}
      {multiline && (
        <textarea
          id={props.id}
          name={props.name}
          ref={inputRef as React.Ref<HTMLTextAreaElement>}
          className={inputClassName}
          data-slot="input"
          data-testid={
            dataTestId ?? (props.id ? `${props.id}-input` : undefined)
          }
          value={props.value}
          defaultValue={props.defaultValue}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readOnly={props.readOnly}
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- autoFocus is intentional UX: passed through from consumer; focus on mount for form inputs
          autoFocus={props.autoFocus}
          autoCapitalize={props.autoCapitalize}
          autoComplete={props.autoComplete}
          autoCorrect={props.autoCorrect}
          spellCheck={props.spellCheck}
          aria-invalid={props['aria-invalid']}
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          aria-describedby={props['aria-describedby']}
          onChange={(e) =>
            props.onChange?.(
              e as unknown as Parameters<NonNullable<typeof props.onChange>>[0],
            )
          }
          onBlur={(e) =>
            props.onBlur?.(
              e as unknown as Parameters<NonNullable<typeof props.onBlur>>[0],
            )
          }
          onFocus={(e) =>
            props.onFocus?.(
              e as unknown as Parameters<NonNullable<typeof props.onFocus>>[0],
            )
          }
          style={maxHeight ? { maxHeight } : undefined}
        />
      )}
      {!multiline && (
        <InputPrimitive
          ref={inputRef}
          className={inputClassName}
          data-slot="input"
          data-testid={
            isFile
              ? undefined
              : (dataTestId ?? (props.id ? `${props.id}-input` : undefined))
          }
          {...props}
          accept={
            isFile && fileInputState
              ? fileInputState.resolvedAccept
              : props.accept
          }
          onChange={(e) => {
            if (isFile && fileInputActions) {
              fileInputActions.handleInputChange(e)
            }
            props.onChange?.(e)
          }}
        />
      )}
      {trailing && (
        <span className="flex shrink-0 items-center" data-slot="input-trailing">
          {trailing}
        </span>
      )}
      {children}
    </span>
  )
}

interface InputDropzoneProps {
  state: FileInputState
  actions: FileInputActions
  className?: string
}

function InputDropzone({ state, actions, className }: InputDropzoneProps) {
  const { t } = useTranslation('ui')
  const {
    files,
    isDragOver,
    isRejected,
    rejectMessage,
    recorder,
    fileType,
    multiple,
    hasFiles,
    hasYoutube,
    youtubeId,
    maxSize,
    maxFiles,
  } = state
  const {
    clearFiles,
    handleClipboardPaste,
    handleYoutubePaste,
    clearYoutubeIds,
  } = actions

  const isTouchOnly =
    typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches

  const dropzoneLabel = (() => {
    if (state.rejectReason && (isDragOver || state.rejectReason))
      return rejectMessage
    if (isDragOver) return t('fileInput.dropHere')
    return t(dropzoneLabelKey(fileType, multiple, isTouchOnly))
  })()

  // Single YouTube mode: show inline
  if (hasYoutube) {
    return (
      <span
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center gap-2 px-4',
          className,
        )}
      >
        <YoutubeIcon className="text-on-surface-secondary size-6 shrink-0" />
        <span className="text-on-surface font-semi text-label-medium flex-1 truncate select-none">
          YouTube: {youtubeId[0]}
        </span>
        <IconButton
          size="sm"
          className="pointer-events-auto shrink-0"
          semanticRole="error"
          onClick={(e) => {
            e.stopPropagation()
            clearYoutubeIds()
          }}
        >
          <Trash2Icon />
          {t('fileInput.removeYoutube')}
        </IconButton>
      </span>
    )
  }

  // Single mode with file selected: show inline
  if (hasFiles && !multiple) {
    const file = files[0]
    return (
      <span
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center gap-2 px-4',
          className,
        )}
      >
        {fileType === 'audio' ? (
          <AudioFilePlayer
            file={file}
            className="pointer-events-auto shrink-0"
          />
        ) : (
          fileTypeIconElement(
            fileType,
            'size-6 shrink-0 text-on-surface-secondary',
          )
        )}
        <span className="text-on-surface font-semi text-label-medium flex-1 truncate select-none">
          {file.name}
        </span>
        <IconButton
          size="sm"
          className="pointer-events-auto shrink-0"
          semanticRole="error"
          onClick={(e) => {
            e.stopPropagation()
            clearFiles()
          }}
        >
          <Trash2Icon />
          {t('fileInput.deleteFile')}
        </IconButton>
      </span>
    )
  }

  // Dropzone (empty single, or always for multiple)
  const metaSegments: string[] = []
  const acceptLabel = getUserFriendlyAccept(state.resolvedAccept)
  if (acceptLabel) metaSegments.push(acceptLabel)
  // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no value"
  if (maxSize != null)
    metaSegments.push(
      `Max ${bytes.format(maxSize, { decimals: 0, capitalize: true })}${multiple ? ' for each' : ''}`,
    )
  // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no value"
  if (maxFiles != null)
    metaSegments.push(`Max ${maxFiles} file${maxFiles === 1 ? '' : 's'}`)
  const metaLine = metaSegments.length > 0 ? metaSegments.join(' · ') : null

  // No action buttons on touch-only devices — just tap to open system picker
  const action = isTouchOnly
    ? null
    : resolveDropzoneAction({
        fileType,
        recorder,
        handleClipboardPaste,
        handleYoutubePaste,
        t,
      })

  return (
    <span
      className={cn(
        'pointer-events-none relative flex w-full flex-col items-center justify-center gap-4',
        className,
      )}
    >
      {fileTypeIconElement(
        fileType,
        cn('text-on-surface-secondary size-8', isRejected && 'invisible'),
        'dropzone',
      )}
      <span
        className={cn(
          'fmt-1 text-md font-bold select-none',
          isRejected ? 'invisible' : 'text-on-surface-secondary',
        )}
      >
        {dropzoneLabel}
      </span>
      {isRejected && (
        <span
          className="text-error text-label-medium pointer-events-none absolute inset-0 flex items-center justify-center font-bold select-none"
          role="alert"
        >
          {rejectMessage}
        </span>
      )}
      {metaLine && (
        <span
          className={cn(
            'text-on-surface-secondary/60 text-label-small mt-1 select-none',
            isRejected && 'invisible',
          )}
        >
          {metaLine}
        </span>
      )}
      {action && (
        <span className={cn('mt-3', isRejected && 'invisible')}>
          <Button
            size="sm"
            variant="outlined"
            className="pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation()
              action.onClick()
            }}
          >
            {action.icon}
            {action.label}
          </Button>
        </span>
      )}
    </span>
  )
}

interface InputFileListProps {
  state: FileInputState
  actions: FileInputActions
  className?: string
}

function InputFileList({ state, actions, className }: InputFileListProps) {
  const { t } = useTranslation('ui')
  const { files, fileType, multiple, youtubeId } = state
  const { removeFile, clearFiles } = actions

  const entries: React.ReactNode[] = []

  // YouTube in multiple mode
  if (youtubeId.length > 0 && fileType === 'video' && multiple) {
    for (const id of youtubeId) {
      entries.push(
        <div
          key={id}
          className="text-label-medium flex min-w-0 items-center gap-2 px-4 py-1.5"
        >
          <YoutubeIcon className="text-on-surface-secondary size-5 shrink-0" />
          <span className="font-semi text-on-surface flex-1 truncate">
            YouTube: {id}
          </span>
          <IconButton
            size="sm"
            semanticRole="error"
            onClick={() => {
              actions.removeYoutubeId(id)
            }}
          >
            <Trash2Icon />
            {t('fileInput.removeYoutube')}
          </IconButton>
        </div>,
      )
    }
  }

  // Files in multiple mode
  if (multiple && files.length > 0) {
    for (const file of files) {
      entries.push(
        <div
          key={file.id}
          className="text-label-medium flex min-w-0 items-center gap-2 px-4 py-1.5"
        >
          <div className="bg-surface-3 flex aspect-square size-10 shrink-0 items-center justify-center rounded-[14px]">
            {fileType === 'audio' ? (
              <AudioFilePlayer file={file} />
            ) : (
              getFileIcon(file)
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-semi text-on-surface truncate">
              {file.name}
            </span>
            <span className="text-on-surface-secondary text-label-small">
              {bytes.format(file.size)}
            </span>
          </div>
          <IconButton
            size="sm"
            semanticRole="error"
            onClick={() => {
              removeFile(file.id)
            }}
          >
            <Trash2Icon />
            {t('fileInput.removeFile')}
          </IconButton>
        </div>,
      )
    }
  }

  if (entries.length === 0) return null

  return (
    <div className={cn('flex flex-col overflow-hidden', className)}>
      {entries}
      {multiple && files.length > 1 && (
        <div className="flex justify-end px-4 py-1.5">
          <Button size="sm" variant="outlined" onClick={clearFiles}>
            <Trash2Icon className="-ms-1" />
            {t('fileInput.removeAll')}
          </Button>
        </div>
      )}
    </div>
  )
}

const dropzoneLabelKeys = {
  file: {
    single: 'fileInput.dropFile',
    multi: 'fileInput.dropFiles',
    singleTouch: 'fileInput.tapFile',
    multiTouch: 'fileInput.tapFiles',
  },
  image: {
    single: 'fileInput.dropImage',
    multi: 'fileInput.dropImages',
    singleTouch: 'fileInput.tapImage',
    multiTouch: 'fileInput.tapImages',
  },
  audio: {
    single: 'fileInput.dropAudio',
    multi: 'fileInput.dropAudios',
    singleTouch: 'fileInput.tapAudio',
    multiTouch: 'fileInput.tapAudios',
  },
  video: {
    single: 'fileInput.dropVideo',
    multi: 'fileInput.dropVideos',
    singleTouch: 'fileInput.tapVideo',
    multiTouch: 'fileInput.tapVideos',
  },
} as const

function dropzoneLabelKey(ft: FileType, multi: boolean, touch: boolean) {
  if (touch) return dropzoneLabelKeys[ft][multi ? 'multiTouch' : 'singleTouch']
  return dropzoneLabelKeys[ft][multi ? 'multi' : 'single']
}

interface DropzoneAction {
  icon: React.ReactNode
  label: string
  onClick: () => void
}

function resolveDropzoneAction({
  fileType,
  recorder,
  handleClipboardPaste,
  handleYoutubePaste,
  t,
}: {
  fileType: FileType
  recorder: FileInputState['recorder']
  handleClipboardPaste: FileInputActions['handleClipboardPaste']
  handleYoutubePaste: FileInputActions['handleYoutubePaste']
  t: ReturnType<typeof useTranslation>['t']
}): DropzoneAction | null {
  if (fileType === 'file' || fileType === 'image') {
    return {
      icon: <ClipboardPasteIcon className="-ms-1" />,
      label: t('fileInput.pasteClipboard'),
      onClick: () => {
        void handleClipboardPaste()
      },
    }
  }
  if (fileType === 'audio') {
    const { recording, seconds } = recorder
    return {
      icon: recording ? (
        <SquareIcon className="-ms-1" />
      ) : (
        <MicIcon className="-ms-1" />
      ),
      label: recording
        ? t('fileInput.stopRecording', { seconds: String(seconds) })
        : t('fileInput.recordAudio'),
      onClick: () => {
        if (recording) recorder.stop()
        else void recorder.start()
      },
    }
  }
  if (fileType === 'video') {
    return {
      icon: <YoutubeIcon className="-ms-1" />,
      label: t('fileInput.pasteYoutube'),
      onClick: () => {
        void handleYoutubePaste()
      },
    }
  }
  return null
}

function fileTypeIconElement(
  ft: FileType,
  className: string,
  variant: 'dropzone' | 'selected' = 'selected',
) {
  if (ft === 'image') return <FileImageIcon className={className} />
  if (ft === 'audio') return <FileMusicIcon className={className} />
  if (ft === 'video') return <FileVideoCameraIcon className={className} />

  return variant === 'dropzone' ? (
    <FilePlusCornerIcon className={className} />
  ) : (
    <FileCheckCornerIcon className={className} />
  )
}

function FileImagePreview({
  file,
  className,
}: {
  file: UFile
  className?: string
}) {
  const [url, setUrl] = useState('')

  useEffect(() => {
    const u = file.createObjectURL()
    // eslint-disable-next-line rc/set-state-in-effect
    setUrl(u)
    // oxlint-disable-next-line rc/url -- local variable u, not a URL literal; false positive from rc/url rule
    return () => {
      URL.revokeObjectURL(u)
    }
  }, [file])

  if (!url) return null
  return (
    <img
      src={url}
      alt={file.name}
      className={cn('size-10 rounded object-cover', className)}
    />
  )
}

function getFileIcon(file: UFile) {
  if (file.isImage) return <FileImagePreview file={file} />
  if (file.isVideo)
    return <VideoIcon className="text-on-surface-secondary size-5 shrink-0" />
  const n = file.name
  if (
    file.type.includes('pdf') ||
    n.endsWith('.pdf') ||
    file.type.includes('word') ||
    n.endsWith('.doc') ||
    n.endsWith('.docx') ||
    n.endsWith('.txt') ||
    n.endsWith('.md')
  )
    return (
      <FileTextIcon className="text-on-surface-secondary size-5 shrink-0" />
    )
  if (
    file.type.includes('zip') ||
    file.type.includes('archive') ||
    n.endsWith('.zip') ||
    n.endsWith('.rar')
  )
    return (
      <FileArchiveIcon className="text-on-surface-secondary size-5 shrink-0" />
    )
  if (
    file.type.includes('excel') ||
    n.endsWith('.xls') ||
    n.endsWith('.xlsx') ||
    n.endsWith('.csv')
  )
    return (
      <FileSpreadsheetIcon className="text-on-surface-secondary size-5 shrink-0" />
    )
  return <FileIcon className="text-on-surface-secondary size-5 shrink-0" />
}

/** Returns label only for specific subtypes (e.g. "PNG, JPEG"). Wildcards like "image/*" return null. */
function getUserFriendlyAccept(accept?: string): string | null {
  if (!accept) return null
  const parts = accept.split(',').map((s) => s.trim())
  // If all entries are wildcards (image/*, audio/*), skip — the dropzone label already says "image"/"audio"
  if (parts.every((p) => p.endsWith('/*'))) return null
  return (
    parts
      .map((part) => {
        const [, sub] = part.split('/')
        if (!sub || sub === '*') return null
        return sub.split('+')[0].toUpperCase()
      })
      // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no value"
      .filter((v): v is string => v != null)
      .filter((v, i, a) => a.indexOf(v) === i)
      .join(', ')
      .replace(/, ([^,]+)$/u, ' or $1') || null
  )
}

function AudioFilePlayer({
  file,
  className,
}: {
  file: UFile
  className?: string
}) {
  const { t } = useTranslation('ui')
  const [playing, setPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const url = file.createObjectURL()
    const audio = new Audio(url)
    audioRef.current = audio
    audio.addEventListener('ended', () => {
      setPlaying(false)
    })
    return () => {
      audio.pause()
      URL.revokeObjectURL(url)
    }
  }, [file])

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
    } else {
      void audio.play()
      setPlaying(true)
    }
  }

  return (
    <IconButton size="sm" className={className} onClick={toggle}>
      {playing ? <PauseIcon /> : <PlayIcon />}
      {playing ? t('fileInput.pauseAudio') : t('fileInput.playAudio')}
    </IconButton>
  )
}

export const inputContainerClass =
  'relative inline-flex min-h-14 w-full px-4 rounded-[16px] border-2 border-outline text-on-surface cursor-text ring-primary/80 has-focus-visible:has-aria-invalid:border-error has-focus-visible:has-aria-invalid:ring-error has-aria-invalid:border-error has-focus-visible:border-outline has-autofill:bg-surface-2 has-disabled:opacity-64 has-disabled:cursor-not-allowed has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[1px] dark:has-autofill:bg-surface-2 dark:has-aria-invalid:ring-error'

/** Bare input: no border/background/padding/radius, 24px tall. Invalid state colors the text via aria-invalid. */
export const inputMinimalClass =
  'text-label-medium relative inline-flex min-h-6 w-full border-none bg-transparent text-on-surface cursor-text has-aria-invalid:text-error has-disabled:opacity-64 has-disabled:cursor-not-allowed'

/** Tinted-background variant: soft surface tint, no border. `aria-invalid` turns into a solid error fill with on-error text (success fill is layered on top via InputField's className). */
export const inputFilledClass =
  'relative inline-flex min-h-14 w-full px-4 rounded-[16px] border-2 border-transparent bg-surface-3 text-on-surface cursor-text has-aria-invalid:bg-error has-aria-invalid:text-on-error has-disabled:opacity-64 has-disabled:cursor-not-allowed'

export { Input, InputDropzone, InputFileList, type InputProps, InputPrimitive }
export {
  type FileInputOptions,
  type FileInputState,
  type FileInputActions,
  useFileInput,
} from '../hooks/use-file-input'
